/**
 * Facet（场景人格面）类型定义
 * v9.1 新增：支持同一身份在不同场景下的说话方式
 * 
 * 参考文档：docs/shared/api-protocol/identity-engine-api.md
 * - 第 8 节：Facet 管理接口
 * - 第 9 节：Quality Gate 接口
 */

// ==================== 场景标签枚举 ====================

/**
 * 场景标签（8 个预设场景）
 * 参考：API 文档 4.2 节 场景标签枚举
 */
export type SceneTag = 
  | 'work_discussion'   // 工作讨论
  | 'relationship'      // 恋爱/暧昧
  | 'family'            // 家人
  | 'friends'           // 朋友闲聊
  | 'public'            // 公开场合
  | 'conflict'          // 冲突/争论
  | 'stress'            // 高压/疲惫
  | 'general'           // 通用/兜底

/**
 * Facet 状态
 * 参考：API 文档 8.1 节 Facet 状态枚举
 */
export type FacetStatus = 
  | 'active'        // 已激活，可用于 Agent
  | 'learning'      // 学习中，显示进度
  | 'draft'         // 草稿，已学习但未发布
  | 'not_started'   // 未开始，暂无语料
  | 'failed'        // 学习失败

// ==================== 测试用例相关枚举 ====================

/**
 * 测试用例类型
 * 参考：API 文档 9.1 节 测试用例类型枚举
 */
export type TestCaseType = 
  | 'boundary'      // 边界测试（必须 100% 通过）
  | 'style'         // 风格测试
  | 'scenario'      // 场景测试
  | 'multi_turn'    // 多轮测试

/**
 * 期望行为
 * 参考：API 文档 9.1 节 期望行为枚举
 */
export type ExpectedBehavior = 
  | 'answer'   // 正常回答
  | 'refuse'   // 拒绝回答
  | 'deflect'  // 转移话题

// ==================== Facet 核心数据模型 ====================

/**
 * Injection Pack - 给 Agent 注入的结构化上下文包
 * 参考：API 文档 8.2 节 Facet 详情响应
 */
export interface InjectionPack {
  persona_core: {
    identity_statement: string
    core_traits: string[]
  }
  style_profile: {
    speaking_style: {
      tone: string
      pace: string
      catchphrases: string[]
    }
    language_patterns: {
      sentence_length: string
      question_style: string
    }
  }
  emotion_patterns: {
    reactions: Record<string, string>
  }
  boundary_guard: {
    hard_boundaries: string[]
    soft_boundaries: string[]
  }
}

/**
 * Feature Store - 统计特征存储
 * 参考：API 文档 8.2 节 Facet 详情响应
 */
export interface FeatureStore {
  observed: {
    avg_sentence_length: number
    question_ratio: number
    emoji_frequency: number
    word_frequency: Record<string, number>
  }
  inferred: {
    personality_traits: string[]
    communication_style: string
  }
}

/**
 * Facet 列表项中的语料简要信息
 * 参考：v9.2 API 文档
 */
export interface FacetCorpusBrief {
  corpus_id: string
  name: string
  item_count: number
}

/**
 * v10.2 场景人格音色绑定信息
 * 参考：API 文档 8.11 节
 * v10.4 更新：新增 instruction 字段
 */
export interface FacetVoiceBinding {
  voice_card_id: string      // 音色卡片 ID
  voice_card_name: string    // 音色卡片名称
  model_id: string           // 绑定的 model_id（使用中的训练模型）
  speech_rate: number        // 语速
  pitch_rate: number         // 音调
  volume: number             // 音量
  instruction?: string       // 🆕 v10.4 情感指令
  bound_at?: string          // 绑定时间
}

/**
 * Facet 列表项
 * 参考：API 文档 8.1 节 GET /identities/{id}/facets 响应
 * v9.2 更新：新增 corpus_list 和 total_item_count
 * v10.2 更新：新增 voice_binding 字段
 */
export interface FacetListItem {
  facet_id: string | null           // null 表示未开始
  identity_id: string
  scene_tag: SceneTag
  scene_name: string
  scene_icon: string
  status: FacetStatus
  version: string | null            // null 表示未学习
  corpus_count: number
  corpus_list?: FacetCorpusBrief[]  // 🆕 v9.2 已关联的语料列表
  total_item_count?: number         // 🆕 v9.2 关联语料的总条数
  test_pass_rate: number | null     // null 表示未测试
  learning_progress: number | null  // 学习中时的进度 0-100
  voice_id?: string | null          // 🆕 v10.0 已发布场景关联的音色 ID
  voice_name?: string | null        // 🆕 v10.0 音色名称（用于 UI 显示）
  // 🆕 v10.1 音色合成配置（从后端返回）
  voice_config?: {
    speech_rate?: number
    pitch_rate?: number
    volume?: number
  } | null
  // 🆕 v10.2 音色绑定信息
  voice_binding?: FacetVoiceBinding | null
  created_at: string | null
  updated_at: string | null
}

/**
 * Facet 详情
 * 参考：API 文档 8.2 节 GET /identities/{id}/facets/{scene_id} 响应
 */
export interface FacetDetail extends FacetListItem {
  scene_description: string
  injection_pack: InjectionPack | null
  feature_store: FeatureStore | null
  learning_job_id: string | null
  learned_at: string | null
}

/**
 * Facet 列表响应
 * 参考：API 文档 8.1 节 响应示例
 */
export interface FacetListResponse {
  items: FacetListItem[]
  summary: {
    total_scenes: number
    active_count: number
    learning_count: number
    draft_count: number
    not_started_count: number
  }
}

// ==================== Quality Gate 数据模型 ====================

/**
 * 测试用例的轮次（多轮测试）
 * 参考：API 文档 9.1 节 多轮测试结构
 */
export interface TestCaseTurn {
  user: string
  expected_behavior: ExpectedBehavior
}

/**
 * 轮次测试结果
 */
export interface TurnResult {
  passed: boolean
  actual_output: string
  error?: string
}

/**
 * 测试用例最近结果
 * 参考：API 文档 9.1 节 last_result 字段
 */
export interface TestCaseLastResult {
  passed: boolean
  actual_output: string
  turn_results?: TurnResult[]  // 多轮测试的轮次结果
  tested_at: string
}

/**
 * 测试用例
 * 参考：API 文档 9.1 节 测试用例列表响应
 */
export interface TestCase {
  id: string
  type: TestCaseType
  category: string
  // 单轮测试字段
  input?: string
  expected_behavior?: ExpectedBehavior
  must_not_contain?: string[]       // 正则表达式数组
  style_markers?: Record<string, string> | null
  // 多轮测试字段
  turns?: TestCaseTurn[]
  // 结果
  last_result: TestCaseLastResult | null
  created_at: string
}

/**
 * 测试结果项
 * 参考：API 文档 9.6 节 results 数组项
 */
export interface TestResultItem {
  test_case_id: string
  passed: boolean
  actual_output?: string
  error_message?: string
  turn_results?: TurnResult[]
}

/**
 * 按类型统计
 * 参考：API 文档 9.1 节 summary.by_type
 */
export interface TestTypeSummary {
  total: number
  passed: number
  pass_rate: number
}

/**
 * 测试概览
 * 参考：API 文档 9.1 节 summary 和 9.6 节 summary
 */
export interface QualityGateSummary {
  total: number
  passed: number
  failed: number
  pending?: number
  pass_rate: number
  by_type: Record<TestCaseType, TestTypeSummary>
}

/**
 * 测试用例列表响应
 * 参考：API 文档 9.1 节 GET /test-cases 响应
 */
export interface TestCaseListResponse {
  items: TestCase[]
  summary: QualityGateSummary
}

/**
 * 测试结果响应
 * 参考：API 文档 9.6 节 GET /test-results 响应
 */
export interface TestResultResponse {
  job_id: string
  status: 'running' | 'completed' | 'failed'
  summary: QualityGateSummary
  can_publish: boolean
  blocking_issues: string[]
  results: TestResultItem[]
  completed_at?: string
}

// ==================== 请求类型 ====================

/**
 * 语料上传请求（v9.1 改造）
 * 参考：API 文档 4.2 节 上传语料请求
 */
export interface UploadCorpusWithSceneRequest {
  target_person: string                 // 必填：被学习人名称
  target_person_aliases?: string[]      // 可选：被学习人别名
  scene_tag: SceneTag                   // 必填：场景标签
  corpus: Array<{
    type: 'weibo' | 'interview' | 'live_chat' | 'dialog' | 'other'
    content: string
    metadata?: Record<string, unknown>
  }>
}

/**
 * 语料列表查询参数（v9.1 扩展）
 * 参考：API 文档 4.1 节 查询参数
 */
export interface CorpusListWithSceneParams {
  type?: string
  scene_tag?: SceneTag          // 新增：场景筛选
  target_person?: string        // 新增：被学习人筛选
  search?: string
  page?: number
  page_size?: number
}

/**
 * 更新 Facet 请求
 * 参考：API 文档 8.3 节 PATCH /facets/{scene_id}
 */
export interface UpdateFacetRequest {
  injection_pack: Partial<InjectionPack>
}

/**
 * 发布 Facet 请求
 * 参考：API 文档 8.4 节 POST /facets/{scene_id}/publish
 * 
 * v10.0 更新：voice_id 必填
 */
export interface PublishFacetRequest {
  voice_id: string      // 🆕 v10.0 必填：场景音色 ID
  description?: string
}

/**
 * 触发 Facet 学习请求
 * 参考：API 文档 8.5 节 POST /facets/{scene_id}/learn
 * v1.7 更新：新增 model 字段，支持选择 LLM 模型
 */
export interface LearnFacetRequest {
  force?: boolean
  model?: string  // v1.7: LLM 模型 ID（如 deepseek-chat, qwen-plus）
}

/**
 * 创建测试用例请求（单轮）
 * 参考：API 文档 9.2 节 POST /test-cases
 */
export interface CreateSingleTurnTestCaseRequest {
  type: Exclude<TestCaseType, 'multi_turn'>
  category: string
  input: string
  expected_behavior: ExpectedBehavior
  must_not_contain?: string[]
  style_markers?: Record<string, string>
}

/**
 * 创建测试用例请求（多轮）
 * 参考：API 文档 9.2 节 POST /test-cases
 */
export interface CreateMultiTurnTestCaseRequest {
  type: 'multi_turn'
  category: string
  turns: TestCaseTurn[]
}

/**
 * 创建测试用例请求（联合类型）
 */
export type CreateTestCaseRequest = CreateSingleTurnTestCaseRequest | CreateMultiTurnTestCaseRequest

/**
 * 更新测试用例请求
 * 参考：API 文档 9.3 节 PUT /test-cases/{case_id}
 */
export interface UpdateTestCaseRequest {
  input?: string
  expected_behavior?: ExpectedBehavior
  must_not_contain?: string[]
  style_markers?: Record<string, string>
  turns?: TestCaseTurn[]
}

/**
 * 运行测试请求
 * 参考：API 文档 9.5 节 POST /run-tests
 */
export interface RunTestsRequest {
  test_case_ids?: string[]
}

// ==================== 响应类型 ====================

/**
 * Facet 学习状态
 * 参考：API 文档 8.6 节 GET /facets/{scene_id}/learn/status
 */
export interface FacetLearningStatus {
  job_id: string
  scene_tag: SceneTag
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  current_stage: string
  stage_description: string
  estimated_remaining_time?: string
  started_at: string
  error_message?: string
}

/**
 * Facet 发布响应
 * 参考：API 文档 8.4 节 POST /publish 响应
 * 
 * v10.0 更新：增加 voice_id 字段
 */
export interface PublishFacetResponse {
  facet_id: string
  scene_tag: SceneTag
  status: FacetStatus
  version: string
  voice_id?: string    // 🆕 v10.0 场景音色 ID
  published_at: string
}

/**
 * Facet 学习响应
 * 参考：API 文档 8.5 节 POST /learn 响应
 */
export interface LearnFacetResponse {
  job_id: string
  scene_tag: SceneTag
  status: string
  started_at: string
  estimated_time: number
}

/**
 * 运行测试响应
 * 参考：API 文档 9.5 节 POST /run-tests 响应
 */
export interface RunTestsResponse {
  job_id: string
  status: 'running' | 'completed'
  total_cases: number
  started_at: string
}

// ==================== 被学习人相关 ====================

/**
 * 被学习人信息
 * 参考：API 文档 4.6 节 GET /target-persons 响应
 */
export interface TargetPerson {
  name: string
  aliases: string[]
  corpus_count: number
  scenes: SceneTag[]
}

/**
 * 被学习人列表响应
 * 参考：API 文档 4.6 节 GET /target-persons 响应
 */
export interface TargetPersonListResponse {
  items: TargetPerson[]
}

// ==================== v9.2 语料-场景关联相关 ====================

/**
 * 场景关联的语料信息
 * 参考：v9.2 GET /facets/{scene}/corpus 响应
 */
export interface FacetCorpusItem {
  corpus_id: string
  name: string
  type: string
  item_count: number
  target_person: string
  associated_at: string
}

/**
 * 场景已关联语料列表响应
 * 参考：v9.2 GET /facets/{scene}/corpus 响应
 */
export interface FacetCorpusListResponse {
  items: FacetCorpusItem[]
}

/**
 * 添加语料到场景请求
 * 参考：v9.2 POST /facets/{scene}/corpus 请求
 */
export interface AssociateCorpusRequest {
  corpus_ids: string[]
}

/**
 * 添加语料到场景响应
 * 参考：v9.2 POST /facets/{scene}/corpus 响应
 */
export interface AssociateCorpusResponse {
  associated_count: number
  corpus_list: FacetCorpusItem[]
}

/**
 * 全局学习请求
 * 参考：v9.2 POST /identities/{id}/learn/all 请求
 */
export interface LearnAllRequest {
  scene_tags?: SceneTag[]
}

/**
 * 全局学习响应
 * 参考：v9.2 POST /identities/{id}/learn/all 响应
 */
export interface LearnAllResponse {
  job_id: string
  scenes_to_learn: Array<{
    scene_tag: SceneTag
    scene_name: string
    corpus_count: number
  }>
  scenes_skipped: SceneTag[]
  jobs: Array<{
    scene_tag: SceneTag
    job_id: string
  }>
  total_scenes: number
}

// ==================== 常量配置 ====================

/**
 * 场景标签配置
 * 参考：API 文档 4.2 节 场景标签枚举
 */
export const SCENE_TAG_CONFIG: Record<SceneTag, {
  label: string
  icon: string
  description: string
}> = {
  work_discussion: { label: '工作讨论', icon: '🏢', description: '直接、目标导向、效率优先' },
  relationship: { label: '恋爱/暧昧', icon: '💕', description: '温柔、关心、情感表达多' },
  family: { label: '家人', icon: '👨‍👩‍👧', description: '亲切、唠叨、关心健康' },
  friends: { label: '朋友闲聊', icon: '👫', description: '放松、吐槽、自嘲' },
  public: { label: '公开场合', icon: '🎤', description: '得体、官方口径、谨慎' },
  conflict: { label: '冲突/争论', icon: '⚡', description: '坚定、据理力争、不退让' },
  stress: { label: '高压/疲惫', icon: '😓', description: '抱怨、简短、急躁' },
  general: { label: '通用/兜底', icon: '🌐', description: '中性、万能' },
}

/**
 * 场景标签数组（用于遍历）
 */
export const SCENE_TAGS: SceneTag[] = [
  'work_discussion',
  'relationship',
  'family',
  'friends',
  'public',
  'conflict',
  'stress',
  'general',
]

/**
 * Facet 状态配置
 * 参考：API 文档 8.1 节 Facet 状态枚举
 */
export const FACET_STATUS_CONFIG: Record<FacetStatus, {
  label: string
  color: string
  bgColor: string
  icon: string
}> = {
  active: { label: '已激活', color: 'text-emerald-600', bgColor: 'bg-emerald-100', icon: '✅' },
  learning: { label: '学习中', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: '🔵' },
  draft: { label: '草稿', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: '⚪' },
  not_started: { label: '未开始', color: 'text-slate-400', bgColor: 'bg-slate-50', icon: '⬜' },
  failed: { label: '失败', color: 'text-red-600', bgColor: 'bg-red-100', icon: '❌' },
}

/**
 * 测试用例类型配置
 * 参考：API 文档 9.1 节 测试用例类型枚举
 */
export const TEST_CASE_TYPE_CONFIG: Record<TestCaseType, {
  label: string
  icon: string
  requiredPassRate: number
  description: string
}> = {
  boundary: { label: '边界测试', icon: '🛡️', requiredPassRate: 1.0, description: '必须 100% 通过' },
  style: { label: '风格测试', icon: '🎨', requiredPassRate: 0.7, description: '≥ 70% 通过' },
  scenario: { label: '场景测试', icon: '🎭', requiredPassRate: 0.7, description: '≥ 70% 通过' },
  multi_turn: { label: '多轮测试', icon: '💬', requiredPassRate: 0.7, description: '≥ 70% 通过' },
}

/**
 * 测试用例类型数组（用于遍历）
 */
export const TEST_CASE_TYPES: TestCaseType[] = ['boundary', 'style', 'scenario', 'multi_turn']

/**
 * 期望行为配置
 * 参考：API 文档 9.1 节 期望行为枚举
 */
export const EXPECTED_BEHAVIOR_CONFIG: Record<ExpectedBehavior, {
  label: string
  description: string
}> = {
  answer: { label: '正常回答', description: 'AI 应该正常回答问题' },
  refuse: { label: '拒绝回答', description: 'AI 应该拒绝回答或表示不便透露' },
  deflect: { label: '转移话题', description: 'AI 应该巧妙地转移话题' },
}

// ==================== 辅助函数 ====================

/**
 * 获取场景标签显示名称
 */
export function getSceneTagLabel(sceneTag: SceneTag): string {
  return SCENE_TAG_CONFIG[sceneTag]?.label || sceneTag
}

/**
 * 获取场景标签图标
 */
export function getSceneTagIcon(sceneTag: SceneTag): string {
  return SCENE_TAG_CONFIG[sceneTag]?.icon || '📋'
}

/**
 * 获取 Facet 状态显示名称
 */
export function getFacetStatusLabel(status: FacetStatus): string {
  return FACET_STATUS_CONFIG[status]?.label || status
}

/**
 * 获取测试用例类型显示名称
 */
export function getTestCaseTypeLabel(type: TestCaseType): string {
  return TEST_CASE_TYPE_CONFIG[type]?.label || type
}

/**
 * 判断是否可以发布（Quality Gate 逻辑）
 * 参考：API 文档 9.6 节 can_publish 判断逻辑
 */
export function canPublishFacet(summary: QualityGateSummary): boolean {
  const boundaryStats = summary.by_type.boundary
  const otherTypes: TestCaseType[] = ['style', 'scenario', 'multi_turn']
  
  // 边界测试必须 100% 通过
  if (boundaryStats && boundaryStats.pass_rate < 1.0) {
    return false
  }
  
  // 其他类型通过率 >= 70%
  for (const type of otherTypes) {
    const stats = summary.by_type[type]
    if (stats && stats.total > 0 && stats.pass_rate < 0.7) {
      return false
    }
  }
  
  return true
}

/**
 * 格式化通过率百分比
 */
export function formatPassRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) {
    return '未测试'
  }
  return `${Math.round(rate * 100)}%`
}
